import { api } from "@/lib/api";
import { showRewarded, initH5Ads, isRewardedReady } from "@/lib/h5ads";

/**
 * Ad Manager — Monetag Rewarded Interstitial.
 * -----------------------------------------------------
 * SDK loader lives in /public/index.html; this module uses the
 * `showRewarded()` wrapper from `./h5ads.js` (filename kept for
 * backwards-compat; underlying provider is Monetag zone 11606704).
 *
 * Flows:
 *   • REWARD placement — opt-in revive. User watches, backend grants 50% HP.
 *   • Audio is muted/paused during the ad per publisher best practice.
 */

// Per-tab guard so we can only claim ONE ad revive per run.
const REVIVE_KEY = "dt_ad_revive_used_v2";

export const hasUsedMonetagRevive = () => {
  try { return sessionStorage.getItem(REVIVE_KEY) === "true"; } catch { return false; }
};

export const markMonetagReviveUsed = () => {
  try { sessionStorage.setItem(REVIVE_KEY, "true"); } catch { /* private mode — best effort */ }
};

export const resetMonetagReviveGuard = () => {
  try { sessionStorage.removeItem(REVIVE_KEY); } catch { /* noop */ }
};

// Backwards-compat: some UI still imports MONETAG_REVIVE_URL to decide
// whether to render the ad button at all. With H5 ads we always want the
// button visible when the SDK is loadable, so return a truthy sentinel.
// (The SDK availability is checked at click-time inside showRewarded.)
export const MONETAG_REVIVE_URL = "h5-ads-adbreak-reward";

// ---- Audio mute/pause during ads --------------------------------------
// Google H5 policy: gameplay audio must be muted while an ad is playing.
function _pauseAudio() {
  try {
    document.querySelectorAll("audio").forEach((a) => {
      if (!a.paused) {
        a.dataset.dtWasPlaying = "1";
        a.pause();
      }
    });
    _log("AD_AUDIO_PAUSE");
  } catch { /* noop */ }
}
function _resumeAudio() {
  try {
    document.querySelectorAll("audio").forEach((a) => {
      if (a.dataset.dtWasPlaying === "1") {
        delete a.dataset.dtWasPlaying;
        // Fire and forget — modern browsers block play() without gesture, that's fine.
        a.play().catch(() => {});
      }
    });
    _log("AD_AUDIO_RESUME");
  } catch { /* noop */ }
}
function _log(evt, extra = {}) {
  try { console.log("[dt-ads]", evt, extra); } catch {}
}

// Ensure the SDK config is pushed at least once on module import.
initH5Ads();

/**
 * handleReviveAd — show a rewarded H5 ad. On success, calls the backend to
 * grant the revive (50% HP + boss re-entry).
 *
 * Return shape (unchanged for backwards compatibility with all death UIs):
 *   { ok: true,  data }                        → success
 *   { ok: false, reason: "ad_unavailable" }     → SDK missing / no fill / ad-blocker / timeout
 *   { ok: false, reason: "already_used" }       → 1-per-run guard tripped
 *   { ok: false, reason: "dismissed" }          → user closed ad early
 *   { ok: false, reason: "backend_failed", detail }
 *
 * NOTE: We migrated FROM Monetag popups TO Google H5 adBreak — the H5 flow does
 * NOT open any windows, so "pop-up blocked" is no longer a possible cause.
 * `ad_unavailable` means ad-blocker extension OR empty sponsor queue OR SDK not
 * loaded. UI callers should treat it as such and show the Ad-Free Pass upsell.
 */
export async function handleReviveAd() {
  if (hasUsedMonetagRevive()) {
    return { ok: false, reason: "already_used" };
  }

  _pauseAudio();
  const ad = await showRewarded({ placement: "revive" });
  _resumeAudio();

  if (!ad.rewarded) {
    const reason = ad.reason === "dismissed" ? "dismissed" : "ad_unavailable";
    return { ok: false, reason, detail: ad.reason || "Ad unavailable" };
  }

  try {
    const data = await api.post("/ads/reward/monetag-revive", { ad_token: ad.token });
    if (data?.ok) {
      markMonetagReviveUsed();
      return { ok: true, data };
    }
    return { ok: false, reason: "backend_failed", detail: "Revive failed." };
  } catch (e) {
    const detail = e?.response?.data?.detail || "Revive service unavailable.";
    return { ok: false, reason: "backend_failed", detail };
  }
}

/**
 * handleAdFreeRevive — bypasses the ad flow when the caller holds an
 * Ad-Free Weekend Pass credit. Backend enforces the credit balance.
 */
export async function handleAdFreeRevive() {
  if (hasUsedMonetagRevive()) {
    return { ok: false, reason: "already_used" };
  }
  try {
    const data = await api.post("/ads/reward/monetag-revive", {});
    if (data?.ok) {
      markMonetagReviveUsed();
      return { ok: true, data };
    }
    return { ok: false, reason: "backend_failed", detail: "Revive failed." };
  } catch (e) {
    const detail = e?.response?.data?.detail || "Revive service unavailable.";
    return { ok: false, reason: "backend_failed", detail };
  }
}

/**
 * showRestartAd — kept as a no-op shim after the Monetag migration.
 * The chosen Monetag integration is REWARDED-ONLY; there is no "start"
 * placement, so this always resolves `{shown:false}` — callers already
 * treat that as "no ad shown, continue the run" and never block on it.
 */
export function showRestartAd() {
  return Promise.resolve({ shown: false, reason: "no_start_placement" });
}

// Re-export utility so callers can gate UI on SDK readiness without adding
// a second import.
export { isRewardedReady };
